import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeftIcon, CubeIcon, WrenchScrewdriverIcon } from '@heroicons/react/20/solid'
import { serviceCasesApi, assetsApi } from '../../lib/api'
import { ServiceCaseForm } from '../../components/forms/ServiceCaseForm'
import { AssetStatusBadge } from '../../components/ui/AssetStatusBadge'
import type { ServiceCaseInput } from '../../../shared/types'

export function ServiceCaseCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  // Pre-selected asset from URL param (e.g., from Asset detail page)
  const preSelectedAssetId = searchParams.get('assetId')
  const preSelectedPartNumber = searchParams.get('partNumber')
  const preSelectedCategory = searchParams.get('category') as ServiceCaseInput['category'] | null

  // Fetch pre-selected asset details for context display
  const { data: preSelectedAsset, isLoading: assetLoading } = useQuery({
    queryKey: ['assets', preSelectedAssetId],
    queryFn: () => assetsApi.get(preSelectedAssetId!),
    enabled: !!preSelectedAssetId,
  })

  const createMutation = useMutation({
    mutationFn: serviceCasesApi.create,
    onSuccess: (newCase) => {
      queryClient.invalidateQueries({ queryKey: ['service-cases'] })
      if (preSelectedAssetId) {
        queryClient.invalidateQueries({ queryKey: ['assets', preSelectedAssetId] })
      }
      navigate(`/service-cases/${newCase.id}`)
    },
  })

  const handleSubmit = (data: ServiceCaseInput) => {
    // Ensure accountId is set from the asset
    if (preSelectedAsset && !data.accountId) {
      data.accountId = preSelectedAsset.accountId
    }
    createMutation.mutate(data)
  }

  const handleCancel = () => {
    if (preSelectedAssetId) {
      navigate(`/assets/${preSelectedAssetId}`)
    } else {
      navigate('/service-cases')
    }
  }

  // Build default values from URL params
  const defaultValues: Partial<ServiceCaseInput> = {
    assetId: preSelectedAssetId || undefined,
    accountId: preSelectedAsset?.accountId,
    category: preSelectedCategory || 'other',
    relatedPartNumber: preSelectedPartNumber || undefined,
    channel: 'portal',
  }

  // If part number is pre-selected, suggest a subject
  if (preSelectedPartNumber && !defaultValues.subject) {
    defaultValues.subject = `Part Request: ${preSelectedPartNumber}`
    defaultValues.category = 'part_request'
  }

  return (
    <div>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to={preSelectedAssetId ? `/assets/${preSelectedAssetId}` : '/service-cases'}
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {preSelectedAssetId ? 'Back to Asset' : 'Back to Service Cases'}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-2 mb-2">
            <WrenchScrewdriverIcon className="h-5 w-5 text-brand-500" />
            <span className="text-lg font-bold text-gray-900">New Service Case</span>
          </div>
          <p className="text-sm text-gray-500">
            Create a new service request for an asset
          </p>
        </div>

        {/* Pre-selected Asset Context */}
        {preSelectedAssetId && (
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Asset Context</h3>
            {assetLoading ? (
              <p className="text-sm text-gray-500">Loading asset...</p>
            ) : preSelectedAsset ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CubeIcon className="h-4 w-4 text-gray-400" />
                  <Link
                    to={`/assets/${preSelectedAsset.id}`}
                    className="text-sm font-medium text-brand-600 hover:text-brand-800"
                  >
                    {preSelectedAsset.serialNumber}
                  </Link>
                  <AssetStatusBadge status={preSelectedAsset.status} />
                </div>
                {preSelectedAsset.account && (
                  <div>
                    <span className="text-xs text-gray-500">Account:</span>
                    <p className="text-sm text-gray-900">{preSelectedAsset.account.name}</p>
                  </div>
                )}
                {preSelectedAsset.productLine && (
                  <div>
                    <span className="text-xs text-gray-500">Product:</span>
                    <p className="text-sm text-gray-900">
                      {preSelectedAsset.productLine} {preSelectedAsset.model && `- ${preSelectedAsset.model}`}
                    </p>
                  </div>
                )}
                {preSelectedAsset.warrantyExpiry && (
                  <div>
                    <span className="text-xs text-gray-500">Warranty:</span>
                    <p className={`text-sm ${new Date(preSelectedAsset.warrantyExpiry) > new Date() ? 'text-green-600' : 'text-red-600'}`}>
                      {new Date(preSelectedAsset.warrantyExpiry) > new Date() ? 'Active' : 'Expired'} - {new Date(preSelectedAsset.warrantyExpiry).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-red-600">Asset not found</p>
            )}
          </div>
        )}

        {/* Part Request Context */}
        {preSelectedPartNumber && (
          <div className="bg-purple-50 p-4 rounded-lg mb-6">
            <h3 className="text-sm font-medium text-purple-900 mb-2">Part Request</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-purple-700 bg-purple-100 px-2 py-1 rounded">
                {preSelectedPartNumber}
              </span>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              This service case will be linked to this part
            </p>
          </div>
        )}

        {/* Help Text */}
        <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Tips</h3>
          <ul className="space-y-2 text-sm text-gray-500">
            <li className="flex items-start gap-2">
              <span className="text-brand-500">•</span>
              <span>Select the correct category to help route your request</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-500">•</span>
              <span>Include error codes or symptoms in the description</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-500">•</span>
              <span>Set priority based on business impact</span>
            </li>
          </ul>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <div className="mb-4">
              <Link
                to={preSelectedAssetId ? `/assets/${preSelectedAssetId}` : '/service-cases'}
                className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                {preSelectedAssetId ? 'Back to Asset' : 'Back to Service Cases'}
              </Link>
            </div>
            <div className="flex items-center gap-x-2 mb-4">
              <WrenchScrewdriverIcon className="h-5 w-5 text-brand-500" />
              <span className="font-bold text-gray-900">New Service Case</span>
            </div>

            {/* Mobile asset context */}
            {preSelectedAsset && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <CubeIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium">{preSelectedAsset.serialNumber}</span>
                  <AssetStatusBadge status={preSelectedAsset.status} />
                </div>
                {preSelectedAsset.account && (
                  <p className="text-sm text-gray-500 mt-1">{preSelectedAsset.account.name}</p>
                )}
              </div>
            )}
          </div>

          {/* Form */}
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 hidden xl:block">
              Create Service Case
            </h2>

            {createMutation.isError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Failed to create service case. Please try again.'}
                </p>
              </div>
            )}

            <ServiceCaseForm
              defaultValues={defaultValues}
              fixedAssetId={preSelectedAssetId || undefined}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={createMutation.isPending}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
